import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import Money from './Money';
import InfoTip from './InfoTip';
import { GlossaryKey } from './glossary';
import { FinanceOverviewResponse, OverviewLinkKey, OverviewPosition, ProofRunStatus } from '../../types/overview';

const TAKEN_AT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** ISO instant → "20 Sept 2026, 03:00 (Dubai)"; unparseable input is shown as-is. */
export function formatTakenAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${TAKEN_AT.format(d)} (Dubai)`;
}

/**
 * Flow C2 step 1: "each card links to the records behind it". The API may send `links`; these are
 * the in-app fallbacks (the list pages the card names — settlements, orders, payouts, proofs).
 */
export const DEFAULT_LINKS: Record<OverviewLinkKey, string> = {
  atStripe: '/finance?tab=payouts',
  heldForCustomers: '/orders',
  owedToGarages: '/settlements',
  owedToCustomers: '/orders',
  owedByGarages: '/settlements',
  ownMoney: '/finance?tab=reports',
  custodyCheck: '/finance?tab=proofs',
  collected: '/orders',
  paidToGarages: '/settlements',
  refunded: '/orders',
  stripeFees: '/finance?tab=reports',
  commission: '/finance?tab=reports',
  settlements: '/settlements',
  stripePayouts: '/finance?tab=payouts',
  // Spec 059 DV-1 — finance admins do not hold the promotions screen, so the card links to reports.
  promotions: '/finance?tab=reports',
  // Spec 068 — Box 1 carries the service fee net + VAT.
  serviceFees: '/finance?tab=reports',
};

export function cardLink(links: FinanceOverviewResponse['links'], key: OverviewLinkKey): string {
  return links?.[key] ?? DEFAULT_LINKS[key];
}

/** Proof status → the admin badge palette; the text is always rendered (colour alone is not enough). */
export function ProofStatusBadge({ status }: { status: ProofRunStatus | string }) {
  const s = String(status).toUpperCase();
  const cls = s === 'PASS' ? 'approved' : s === 'FAIL' ? 'rejected' : 'pending';
  return <span className={`admin-badge ${cls}`} data-testid="proof-status">{s}</span>;
}

interface CardProps {
  title: string;
  glossaryKey: GlossaryKey;
  headline: ReactNode;
  to: string;
  linkLabel: string;
  testId: string;
  /** Muted line under the headline (e.g. "as of …", "derived"). */
  caption?: ReactNode;
  children?: ReactNode;
}

/** Position card: title carries the ⓘ for the headline key, the figure is a Money without a second icon. */
function PositionCard({ title, glossaryKey, headline, to, linkLabel, testId, caption, children }: CardProps) {
  return (
    <div className="admin-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }} data-testid={testId}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
        {title}
        <InfoTip glossaryKey={glossaryKey} />
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{headline}</div>
      {caption && <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>{caption}</div>}
      {children}
      <div style={{ marginTop: 'auto', paddingTop: '0.5rem', fontSize: '0.75rem' }}>
        <Link to={to}>{linkLabel} →</Link>
      </div>
    </div>
  );
}

/** A labelled sub-line inside a card: "Available … AED 9,800.00". */
function SubLine({ label, amount, glossaryKey, testId }: { label: string; amount: number | null | undefined; glossaryKey: GlossaryKey; testId?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.8125rem' }} data-testid={testId}>
      <span style={{ color: 'var(--admin-text-muted)', display: 'inline-flex', alignItems: 'center' }}>
        {label}<InfoTip glossaryKey={glossaryKey} />
      </span>
      <Money amount={amount} glossaryKey={glossaryKey} hideTip />
    </div>
  );
}

interface PositionCardsProps {
  position: OverviewPosition;
  asAt: string;
  links: FinanceOverviewResponse['links'];
}

/**
 * Spec 053 Flow C2 step 1 / AC-28b — the position cards as at a date. Every figure is a
 * `<Money glossaryKey>` (BR-31). "Owed to garages" expands to its four subledger buckets; the
 * custody card shows the last Proof 2 on or before the date and links to the Proofs tab.
 */
export default function PositionCards({ position, asAt, links }: PositionCardsProps) {
  const [bucketsOpen, setBucketsOpen] = useState(false);
  const { atStripe, gatewayClearing, inCustomerFundsBank, heldForCustomers, owedToGarages, owedToCustomers, owedByGarages, ownMoney, custodyCheck } = position;
  const bucketsTotal = owedToGarages.buckets.hold + owedToGarages.buckets.eligible + owedToGarages.buckets.draft + owedToGarages.buckets.awaitingPayment;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(16rem, 100%), 1fr))', gap: '1rem' }} data-testid="position-cards">
      <PositionCard
        title="At Stripe"
        glossaryKey="stripeTotal"
        headline={<Money amount={atStripe.total} glossaryKey="stripeTotal" hideTip />}
        caption={
          <span data-testid="stripe-as-of">
            {atStripe.takenAt
              ? `${atStripe.source === 'LIVE' ? 'live' : 'last snapshot'} · as of ${formatTakenAt(atStripe.takenAt)}`
              : 'No snapshot yet'}
            <InfoTip glossaryKey="atStripeSource" />
          </span>
        }
        to={cardLink(links, 'atStripe')}
        linkLabel="Stripe payouts"
        testId="card-atStripe"
      >
        <SubLine label="Available" amount={atStripe.available} glossaryKey="stripeAvailable" />
        <SubLine label="Pending" amount={atStripe.pending} glossaryKey="stripePending" />
        <SubLine label="Clearing (Stripe)" amount={gatewayClearing.stripe} glossaryKey="gatewayClearingStripe" />
        {gatewayClearing.nonStripe !== 0 && (
          <SubLine label="Clearing (non-Stripe, unverified)" amount={gatewayClearing.nonStripe} glossaryKey="gatewayClearingNonStripe" testId="clearing-non-stripe" />
        )}
        {gatewayClearing.tabbyReceivable != null && (
          <SubLine label="Tabby receivable" amount={gatewayClearing.tabbyReceivable} glossaryKey="gatewayClearingTabbyReceivable" testId="clearing-tabby-receivable" />
        )}
      </PositionCard>

      <PositionCard
        title="Settled from Stripe"
        glossaryKey="inCustomerFundsBank"
        headline={<Money amount={inCustomerFundsBank.amount} glossaryKey="inCustomerFundsBank" hideTip />}
        caption="less garage payments"
        to={cardLink(links, 'stripePayouts')}
        linkLabel="Stripe payouts"
        testId="card-inCustomerFundsBank"
      />

      <PositionCard
        title="Held for customers"
        glossaryKey="heldForCustomers"
        headline={<Money amount={heldForCustomers.ledger} glossaryKey="heldForCustomers" hideTip />}
        to={cardLink(links, 'heldForCustomers')}
        linkLabel="Orders"
        testId="card-heldForCustomers"
      />

      <PositionCard
        title="Owed to garages"
        glossaryKey="owedToGarages"
        headline={<Money amount={owedToGarages.ledger} glossaryKey="owedToGarages" hideTip />}
        caption={owedToGarages.bucketsAsAt === 'CURRENT' ? 'buckets show the current state, not the date' : undefined}
        to={cardLink(links, 'owedToGarages')}
        linkLabel="Settlements"
        testId="card-owedToGarages"
      >
        <button
          type="button"
          className="admin-btn-text"
          aria-expanded={bucketsOpen}
          aria-controls="owed-to-garages-buckets"
          style={{ alignSelf: 'flex-start', padding: 0, fontSize: '0.75rem' }}
          onClick={() => setBucketsOpen((o) => !o)}
        >
          {bucketsOpen ? 'Hide buckets' : 'Show buckets'}
        </button>
        {bucketsOpen && (
          <div id="owed-to-garages-buckets" data-testid="owed-to-garages-buckets" style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
            <SubLine label="In hold" amount={owedToGarages.buckets.hold} glossaryKey="owedToGaragesHold" />
            <SubLine label="Eligible, not settled" amount={owedToGarages.buckets.eligible} glossaryKey="owedToGaragesEligible" />
            <SubLine label="In DRAFT" amount={owedToGarages.buckets.draft} glossaryKey="owedToGaragesDraft" />
            <SubLine label="Approved, awaiting payment" amount={owedToGarages.buckets.awaitingPayment} glossaryKey="owedToGaragesAwaitingPayment" />
            {owedToGarages.unledgered !== 0 && (
              <SubLine label={`Not in ledger (buckets ${bucketsTotal.toFixed(2)} − ledger)`} amount={owedToGarages.unledgered} glossaryKey="owedToGaragesUnledgered" testId="owed-to-garages-unledgered" />
            )}
          </div>
        )}
      </PositionCard>

      <PositionCard
        title="Refunds approved, not yet paid"
        glossaryKey="owedToCustomers"
        headline={<Money amount={owedToCustomers.ledger} glossaryKey="owedToCustomers" hideTip />}
        to={cardLink(links, 'owedToCustomers')}
        linkLabel="Orders"
        testId="card-owedToCustomers"
      />

      <PositionCard
        title="Owed by garages"
        glossaryKey="owedByGarages"
        headline={<Money amount={owedByGarages.ledger} glossaryKey="owedByGarages" hideTip />}
        to={cardLink(links, 'owedByGarages')}
        linkLabel="Settlements"
        testId="card-owedByGarages"
      >
        <SubLine label={`Open clawbacks (${owedByGarages.openClawbacks.count})`} amount={owedByGarages.openClawbacks.amount} glossaryKey="openClawbacks" />
        <SubLine label={`Carried balances (${owedByGarages.carriedBalances.count})`} amount={owedByGarages.carriedBalances.amount} glossaryKey="openCarriedBalances" />
      </PositionCard>

      <PositionCard
        title="Garagat's own money"
        glossaryKey="ownMoneyTotal"
        headline={<Money amount={ownMoney.total} glossaryKey="ownMoneyTotal" hideTip />}
        caption="earned, not withdrawn"
        to={cardLink(links, 'ownMoney')}
        linkLabel="Reports"
        testId="card-ownMoney"
      >
        <SubLine label="Earned (net)" amount={ownMoney.earnedNet} glossaryKey="ownMoneyEarnedNet" />
        <SubLine label="VAT held" amount={ownMoney.vatHeld} glossaryKey="ownMoneyVatHeld" />
        <SubLine label="Withdrawn" amount={ownMoney.withdrawn} glossaryKey="ownMoneyWithdrawn" />
      </PositionCard>

      <PositionCard
        title="Custody check"
        glossaryKey="custodyVariance"
        headline={custodyCheck
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}><ProofStatusBadge status={custodyCheck.status} /><Money amount={custodyCheck.variance} glossaryKey="custodyVariance" hideTip /></span>
          : <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.875rem', fontWeight: 500 }} data-testid="custody-none">No Proof 2 run on or before {asAt}</span>}
        caption={custodyCheck ? `Proof 2 for ${custodyCheck.runDate}` : undefined}
        to={cardLink(links, 'custodyCheck')}
        linkLabel="Proofs"
        testId="card-custodyCheck"
      />
    </div>
  );
}
